import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Spinner component used for loading state.
 */
const Spinner = ({ className = '' }) => (
  <svg
    className={`animate-spin h-5 w-5 ${className}`}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

/* ─── Design tokens ──────────────────────────────────────────────────── */

const sizeClasses = {
  sm: 'px-4 py-2 text-sm gap-1.5',
  md: 'px-6 py-2.5 text-base gap-2',
  lg: 'px-8 py-3.5 text-lg gap-2.5',
};

const variantClasses = {
  primary: [
    'bg-gradient-to-r from-purple-600 to-pink-600 text-white',
    'hover:from-purple-700 hover:to-pink-700',
    'shadow-lg shadow-purple-500/25 hover:shadow-xl hover:shadow-purple-500/30',
    'focus-visible:ring-purple-300',
  ].join(' '),

  secondary: [
    'border border-purple-200 text-purple-700 bg-white',
    'hover:bg-purple-50 hover:border-purple-300',
    'shadow-sm hover:shadow',
    'focus-visible:ring-purple-200',
  ].join(' '),

  ghost: [
    'text-purple-600 bg-transparent',
    'hover:bg-purple-50',
    'focus-visible:ring-purple-200',
  ].join(' '),

  danger: [
    'bg-red-600 text-white',
    'hover:bg-red-700',
    'shadow-sm hover:shadow',
    'focus-visible:ring-red-300',
  ].join(' '),

  inverted: [
    'bg-white text-purple-600',
    'hover:bg-purple-50',
    'shadow-lg hover:shadow-xl',
    'focus-visible:ring-white/50',
  ].join(' '),
};

const baseClasses = [
  'inline-flex items-center justify-center',
  'rounded-xl font-semibold',
  'transition-all duration-200',
  'focus-visible:outline-none focus-visible:ring-4',
  'disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100',
  'select-none',
].join(' ');

const interactiveClasses = 'hover:scale-[1.02] active:scale-[0.98]';

/* ─── Component ──────────────────────────────────────────────────────── */

/**
 * Reusable Button component.
 *
 * Renders as `<Link>` when `to` prop is provided, otherwise `<button>`.
 * Custom element via `as` prop.
 *
 * @param {object} props
 * @param {'primary'|'secondary'|'ghost'|'danger'|'inverted'} props.variant
 * @param {'sm'|'md'|'lg'} props.size
 * @param {boolean} props.loading
 * @param {string} props.loadingText - Text to show while loading (optional)
 * @param {boolean} props.fullWidth
 * @param {string} props.to - react-router-dom path; renders as Link
 * @param {React.ElementType} props.as - custom element type
 * @param {string} props.className - additional classes merged at the end
 */
const Button = React.forwardRef(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      loadingText,
      fullWidth = false,
      disabled = false,
      to,
      as,
      className = '',
      children,
      ...rest
    },
    ref
  ) => {
    const isDisabled = disabled || loading;

    const classes = [
      baseClasses,
      sizeClasses[size] || sizeClasses.md,
      variantClasses[variant] || variantClasses.primary,
      !isDisabled && interactiveClasses,
      fullWidth && 'w-full',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const content = (
      <>
        {loading && (
          <Spinner
            className={
              variant === 'secondary' || variant === 'ghost'
                ? 'text-purple-600'
                : 'text-white'
            }
          />
        )}
        {loading && loadingText ? loadingText : children}
      </>
    );

    // Determine which element to render
    const Component = as || (to ? Link : 'button');

    const sharedProps = {
      ref,
      className: classes,
      ...(Component === 'button' && { disabled: isDisabled, type: rest.type || 'button' }),
      ...(to && { to }),
      ...rest,
    };

    return <Component {...sharedProps}>{content}</Component>;
  }
);

Button.displayName = 'Button';

export { Spinner };
export default Button;
